import React, { useState } from 'react';
import { Box, Typography, Container, Grid, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

function Advantages() {
    const [expanded, setExpanded] = useState(false);

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    return (
        <Box sx={{ backgroundColor: '#fff', py: 10 }}>
            <Container maxWidth="lg">
                {/* Page Header */}
                <Box textAlign="center" mb={8}>
                    <Typography variant="h6" sx={{ color: '#7e57c2', fontWeight: 'bold', mb: 1, letterSpacing: '2px', textTransform: 'uppercase', fontSize: '14px' }}>
                        Why Choose Us
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#003366', textTransform: 'uppercase' }}>
                        ADVANTAGES & BENEFITS
                    </Typography>
                    <Typography sx={{ fontSize: '16px', color: '#666', mt: 2, maxWidth: '700px', mx: 'auto', lineHeight: 1.8 }}>
                        Fem Tech Solution offers more than products—we deliver practical benefits that improve hygiene, safety, and institutional efficiency.
                    </Typography>
                </Box>

                {/* Key Advantages - Double Column */}
                <Box sx={{ mb: 12 }}>
                    <Grid container spacing={3}>
                        {[
                            'Women-centered infrastructure ensuring dignity, privacy, and comfort',
                            'Reliable & low-maintenance machines for long-term use',
                            'Improved hygiene standards in schools and workplaces',
                            'Easy deployment with minimal disruption',
                            'Scalable solutions for small institutions to large campuses',
                            'CSR-ready impact with clean documentation and reporting',
                            'Cost-effective long-term investment with high social return',
                            'Eco-friendly disposal protecting the environment'
                        ].map((item, index) => (
                            <Grid item xs={12} md={6} key={index}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    p: 3,
                                    backgroundColor: '#f9f9fc',
                                    borderRadius: '16px',
                                    border: '1px solid #f0f0f0',
                                    height: '100%',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 10px 30px rgba(126, 87, 194, 0.1)',
                                        borderColor: '#e1bee7',
                                        backgroundColor: '#fff'
                                    }
                                }}>
                                    <CheckCircleOutlineIcon sx={{ color: '#7e57c2', mr: 2, fontSize: '28px' }} />
                                    <Typography sx={{ fontSize: '15px', color: '#444', fontWeight: '500', lineHeight: 1.6 }}>
                                        {item}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FAQ Section - Double Column */}
                <Box>
                    <Typography variant="h4" sx={{ mb: 6, fontWeight: 'bold', color: '#003366', textAlign: 'center', textTransform: 'uppercase' }}>
                        FREQUENTLY ASKED QUESTIONS
                    </Typography>
                    <Grid container spacing={4}>
                        {(() => {
                            const faqs = [
                                { q: 'Where can Fem Tech Solution machines be installed?', a: 'Our machines are suitable for schools, colleges, workplaces, hospitals, hostels, and public institutions.' },
                                { q: 'Are the machines safe and easy to use?', a: 'Yes. All machines are designed with safety features and simple user-friendly operation.' },
                                { q: 'Do you provide installation support?', a: 'Yes. We guide institutions through installation and setup to ensure smooth deployment.' },
                                { q: 'Is maintenance required frequently?', a: 'No. Our machines are low-maintenance and built for high-usage environments.' },
                                { q: 'Can organizations install through CSR funding?', a: 'Yes. Our solutions are CSR-ready and ideal for corporate social responsibility projects.' },
                                { q: 'How do vending machines get refilled?', a: 'Refilling is simple and can be handled by trained staff or designated coordinators.' },
                                { q: 'Are incinerators environmentally safe?', a: 'Yes. Our incinerators are designed for eco-friendly, smoke-controlled disposal.' },
                                { q: 'Do your machines come with warranty?', a: 'Yes. All products are covered by warranty and supported with service assistance.' },
                                { q: 'Can machines be customized with branding?', a: 'Yes. Institutions and CSR partners can add their logo or branding.' },
                                { q: 'Do you support awareness programs?', a: 'Yes. We encourage menstrual hygiene awareness initiatives along with infrastructure deployment.' },
                                { q: 'How can we partner with Fem Tech Solution?', a: 'You can contact us through the enquiry form or email to discuss installation or partnership.' }
                            ];
                            const mid = Math.ceil(faqs.length / 2);
                            const leftFaqs = faqs.slice(0, mid);
                            const rightFaqs = faqs.slice(mid);

                            const renderFaqs = (faqList, offset) => faqList.map((faq, index) => {
                                const actualIndex = index + offset;
                                return (
                                    <Accordion
                                        key={actualIndex}
                                        expanded={expanded === actualIndex}
                                        onChange={handleChange(actualIndex)}
                                        sx={{
                                            backgroundColor: '#fff',
                                            borderRadius: '12px !important',
                                            border: '1px solid #eaeaea',
                                            boxShadow: '0 4px 15px rgba(0,0,0,0.02)',
                                            mb: 2,
                                            '&:before': { display: 'none' },
                                            '&.Mui-expanded': {
                                                margin: '0 0 16px 0',
                                                borderColor: '#7e57c2',
                                                boxShadow: '0 8px 25px rgba(126, 87, 194, 0.1)'
                                            }
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={expanded === actualIndex ? <RemoveIcon sx={{ color: '#7e57c2' }} /> : <AddIcon sx={{ color: '#7e57c2' }} />}
                                            sx={{ p: 2, '& .MuiAccordionSummary-content': { my: 0 } }}
                                        >
                                            <Typography sx={{ fontWeight: '600', color: expanded === actualIndex ? '#7e57c2' : '#333', fontSize: '15px' }}>
                                                {faq.q}
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails sx={{ px: 3, pb: 3, pt: 0 }}>
                                            <Typography sx={{ color: '#666', fontSize: '15px', lineHeight: 1.7 }}>
                                                {faq.a}
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                );
                            });

                            return (
                                <>
                                    <Grid item xs={12} md={6}>
                                        {renderFaqs(leftFaqs, 0)}
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        {renderFaqs(rightFaqs, mid)}
                                    </Grid>
                                </>
                            );
                        })()}
                    </Grid>
                </Box>
            </Container>
        </Box>
    );
}

export default Advantages;
